import type { ConversationId } from '@cyphex/types';
import type { OutboxItem } from '../types';
import { outboxDb } from '../lib/outboxDb';
import { createStore } from './createStore';

interface OutboxState {
  items: readonly OutboxItem[];
  /** Bumped on every mutation so derived snapshots can be cached safely. */
  version: number;
  hydrated: boolean;
}

const store = createStore<OutboxState>({ items: [], version: 0, hydrated: false });

const EMPTY: readonly OutboxItem[] = [];
const byConversation = new Map<string, { version: number; items: readonly OutboxItem[] }>();

function commit(items: readonly OutboxItem[], hydrated = true): void {
  store.set((previous) => ({ items, version: previous.version + 1, hydrated }));
}

export const outboxStore = {
  subscribe: store.subscribe,
  getState: store.get,

  /** Reads the durable queue back after a reload. Idempotent. */
  async hydrate(): Promise<void> {
    if (store.get().hydrated) return;
    commit(await outboxDb.all());
  },

  /** Stable per-conversation snapshot — identity only changes on mutation. */
  select(conversationId: ConversationId): readonly OutboxItem[] {
    const { items, version } = store.get();
    const cached = byConversation.get(conversationId);
    if (cached && cached.version === version) return cached.items;
    const filtered = items.filter((item) => item.conversationId === conversationId);
    const next = filtered.length === 0 ? EMPTY : filtered;
    byConversation.set(conversationId, { version, items: next });
    return next;
  },

  async enqueue(item: OutboxItem): Promise<void> {
    await outboxDb.put(item);
    commit([...store.get().items, item]);
  },

  async update(clientMsgId: string, patch: Partial<OutboxItem>): Promise<void> {
    const current = store.get().items.find((item) => item.clientMsgId === clientMsgId);
    if (!current) return;
    const next = { ...current, ...patch };
    await outboxDb.put(next);
    commit(store.get().items.map((item) => (item.clientMsgId === clientMsgId ? next : item)));
  },

  async remove(clientMsgId: string): Promise<void> {
    await outboxDb.remove(clientMsgId);
    commit(store.get().items.filter((item) => item.clientMsgId !== clientMsgId));
  },

  /** Items eligible to send now, oldest first. */
  due(now: number): readonly OutboxItem[] {
    return store.get().items.filter(
      (item) => item.state !== 'sending' && item.state !== 'sent' && item.nextAttemptAt <= now,
    );
  },
};
